import flatMap from 'lodash/flatMap'

import { AUTH_TOKEN_STORAGE_KEY } from '@/constants'
import router from '@/router'
import client from '@/utils/client'
import storage from '@/utils/storage'
import urls from '@/utils/urls'

import * as mutationTypes from '../mutationTypes'

const state = {
  authToken: storage.load(AUTH_TOKEN_STORAGE_KEY),
  loading: false,
  error: null
}

const getters = {
  isLoggedIn: state => !!state.authToken,
  isLoading: state => state.loading,
  error: state => state.error
}

const mutations = {
  [mutationTypes.REGISTER_REQUEST] (state) {
    state.loading = true
    state.error = null
  },

  [mutationTypes.REGISTER_SUCCESS] (state, payload) {
    state.loading = false
    state.authToken = null
  },

  [mutationTypes.REGISTER_ERROR] (state, payload) {
    state.loading = false
    state.error = payload
  },

  [mutationTypes.LOGIN_REQUEST] (state) {
    state.loading = true
    state.error = null
  },

  [mutationTypes.LOGIN_SUCCESS] (state, payload) {
    // Store tokens into local storage
    storage.save(AUTH_TOKEN_STORAGE_KEY, payload)

    state.loading = false
    state.authToken = payload
  },

  [mutationTypes.LOGIN_ERROR] (state, payload) {
    state.loading = false
    state.error = payload
  },

  [mutationTypes.LOGOUT] (state) {
    // Remove tokens from local storage
    storage.remove(AUTH_TOKEN_STORAGE_KEY)

    state.loading = false
    state.authToken = null
  }
}

async function performLogin ({dispatch, commit}, token, next = null) {
  // Store auth token to storage
  commit(mutationTypes.LOGIN_SUCCESS, token)
  let currentUser

  // Get profile
  try {
    currentUser = await dispatch('profile/getCurrentUser', null, { root: true })
  } catch (c) {
    dispatch('flash/showMessage', { message: 'Unable to log in', type: 'error' })
    return
  }

  // Asynchronously get a list of songs we have already have access to
  dispatch('song/refreshSongsWithAccess', null, { root: true })

  if (currentUser.artist) {
    commit('artist/setArtist', currentUser.artist, { root: true })
  } else {
    commit('artist/setArtist', {}, { root: true })
  }

  // Asynchronously get notifications
  dispatch('realtime/initNotifications', null, { root: true })

  // Asynchronously get a list of followed artists
  dispatch('profile/refreshFollowedArtists', null, { root: true })

  // If the artist isn't completely onboarded yet, send them to onboarding
  if (currentUser.artist && !currentUser.artist.stripe_account_id) {
    router.push({ name: 'artist-onboarding' })
    return
  }

  // Redirect to Home
  if (next) {
    router.push(next)
  } else {
    router.push({ name: 'fan-home' })
  }
}

const actions = {
  async register ({ commit, dispatch }, payload) {
    const { username, email, password, recaptchaToken, next = null } = payload

    try {
      commit(mutationTypes.REGISTER_REQUEST)

      await client
        .post(urls.auth.register(), {
          username,
          email,
          password1: password,
          password2: password,
          recaptcha_token: recaptchaToken
        })

      commit(mutationTypes.REGISTER_SUCCESS)

      dispatch('login', { username, password, next })
      return true
    } catch (e) {
      commit(mutationTypes.REGISTER_ERROR, flatMap(e.response.data))
      return false
    }
  },

  async login ({ commit, dispatch }, payload) {
    const { username, password, next } = payload

    try {
      commit(mutationTypes.LOGIN_REQUEST)

      const resp = await client
        .post(urls.auth.login(), {
          username,
          password
        })

      await performLogin({ commit, dispatch }, resp.data.key, next)
    } catch (e) {
      if (e.response && e.response.data) {
        commit(mutationTypes.LOGIN_ERROR, flatMap(e.response.data))
      }
    }
  },

  logout ({ commit, dispatch }) {
    commit(mutationTypes.LOGOUT)
    commit(`profile/${mutationTypes.CLEAR_CURRENT_USER}`, null, { root: true })
    commit('artist/setArtist', {}, { root: true })
    dispatch('social/logout', null, { root: true })
    dispatch('realtime/stopLiveChat', null, { root: true })
    dispatch('realtime/stopNotifications', null, { root: true })
    router.push('/')
  },

  async socialAuth (ctx, { provider, token }) {
    const resp = await client.post(urls.auth.social(provider), { token })
    await performLogin(ctx, resp.data.key)
  },

  async resetPassword (ctx, email) {
    return client.post(urls.auth.resetPassword(), {email: email})
  },

  async confirmResetPassword (ctx, { uid, token, password }) {
    return client.post(urls.auth.confirmResetPassword(), {
      uid,
      token,
      new_password1: password,
      new_password2: password
    })
  },

  async changePassword (ctx, newPassword) {
    try {
      let response = await client.post(urls.auth.changePassword(), {
        new_password1: newPassword,
        new_password2: newPassword
      })
      return response
    } catch (e) {
      let msg = 'Can\'t change password'
      if (e.response && e.response.data) {
        if (e.response.data.new_password1) msg = e.response.data.new_password1
        if (e.response.data.new_password2) msg = e.response.data.new_password2
      }
      throw new Error(msg)
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/store/modules/auth.js